import { getConfig } from '@/utils/config';
import { toast } from 'sonner';
import { z } from 'zod';

export async function apiWrapper<
  SchemaType extends z.ZodType | undefined,
  IsPaginated extends boolean,
>({
  accessToken,
  baseUrl = getConfig().api.baseUrl,
  body: bodyParam,
  endpoint,
  errorMessage,
  isPaginated,
  method,
  responseSchema,
  searchParams,
}: {
  accessToken: string | undefined;
  baseUrl?: string;
  body?: Record<string, unknown>;
  endpoint: string;
  errorMessage?: string;
  isPaginated: IsPaginated;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  responseSchema: SchemaType;
  searchParams?: Record<string, string | boolean | number>;
}): Promise<
  SchemaType extends z.ZodType
    ? IsPaginated extends true
      ? PaginatedResponse<z.infer<SchemaType>>
      : z.infer<SchemaType>
    : void
> {
  const url = new URL(`${baseUrl}${endpoint}`);
  if (searchParams) {
    Object.entries(searchParams).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });
  }

  const headers: HeadersInit = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const body =
    method === 'GET' || method === 'DELETE'
      ? undefined
      : JSON.stringify(bodyParam);

  const response = await fetch(url.toString(), {
    body,
    headers,
    method,
  });

  if (!response.ok) {
    if (response.status === 409 && errorMessage) {
      toast.info(errorMessage);
    } else {
      // todo: show fine-grained error toasts - otherwise it's confusing
      // toast.error(errorMessage);
    }

    throw new Error(
      `API Error: ${response.status} ${response.statusText} for endpoint ${endpoint}`,
    );
  }

  if (!responseSchema) {
    return undefined as never;
  }

  if (response.status === 204) {
    console.warn(
      `API returned 204 No Content, but a response schema was provided for endpoint: ${endpoint}`,
    );
    throw new Error(
      `API Error: Expected content for schema, but got ${response.status} No Content from ${endpoint}`,
    );
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    const jsonData = (await response.json()) as unknown;

    const schemaToParse: z.ZodType = isPaginated
      ? paginatedResponseSchema(responseSchema)
      : responseSchema;

    const parsedData = schemaToParse.safeParse(jsonData);

    if (!parsedData.success) {
      console.error(
        `API response parsing failed for endpoint: ${endpoint}`,
        parsedData.error,
      );

      // todo: implement sentry error tracking
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      // @ts-expect-error - this is not safe and should be fixed
      return jsonData as z.infer<SchemaType>;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    // @ts-expect-error - zod v4 added a typescript error
    return parsedData.data;
  } else {
    console.error(
      `API did not return JSON for endpoint: ${endpoint}. Content-Type: ${contentType}, Status: ${response.status}`,
    );
    throw new Error(
      `API Error: Expected JSON content for ${endpoint} because a response schema was provided, but received status ${response.status} with Content-Type: ${contentType}.`,
    );
  }
}

const paginatedResponseSchema = <S extends z.ZodType>(
  contentSchema: S,
) =>
  z.object({
    content: z.array(contentSchema),
    pageable: z.object({
      pageNumber: z.number(),
      pageSize: z.number(),
      sort: z.object({
        empty: z.boolean(),
        sorted: z.boolean(),
        unsorted: z.boolean(),
      }),
      offset: z.number(),
      paged: z.boolean(),
      unpaged: z.boolean(),
    }),
    last: z.boolean(),
    totalElements: z.number(),
    totalPages: z.number(),
    first: z.boolean(),
    size: z.number(),
    number: z.number(),
    sort: z.object({
      empty: z.boolean(),
      sorted: z.boolean(),
      unsorted: z.boolean(),
    }),
    numberOfElements: z.number(),
    empty: z.boolean(),
  });

export type PaginatedResponse<T> = {
  content: T[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  last: boolean;
  totalElements: number;
  totalPages: number;
  first: boolean;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  empty: boolean;
};
